import React from 'react';
import { Box, Paper, Typography, Alert, Chip, Grid, Card, CardContent, Skeleton, TextField, MenuItem, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { CheckCircle, Schedule, Error, TrendingUp, People, DevicesOther, Visibility } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { youtubeAPI, dataAPI } from '../../services/api';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import SectionTitle from '../crt/SectionTitle';
import Panel from '../crt/Panel';

const ReportingData: React.FC = () => {
  // Fetch reporting summary data
  const { data: reportingData, isLoading } = useQuery({
    queryKey: ['reporting-summary', 30],
    queryFn: () => youtubeAPI.getReportingSummary(30),
    staleTime: 300000 // 5 minutes
  });

  // Fetch comprehensive data for accurate metrics
  const { data: comprehensiveData } = useQuery({
    queryKey: ['comprehensive-reporting', 30],
    queryFn: () => youtubeAPI.getComprehensiveDashboard(30),
    staleTime: 300000 // 5 minutes
  });

  // List all YouTube Reporting API reports we're collecting
  const reports = [
    { name: 'Channel Basic Stats', type: 'channel_basic_a3', status: 'active' },
    { name: 'Channel Province', type: 'channel_province_a3', status: 'active' },
    { name: 'Channel Traffic Source', type: 'channel_traffic_source_a2', status: 'active' },
    { name: 'Channel Device/OS', type: 'channel_device_os_a2', status: 'active' },
    { name: 'Channel Demographics', type: 'channel_demographics_a2', status: 'active' },
    { name: 'Channel Sharing Service', type: 'channel_sharing_service_a2', status: 'active' },
    { name: 'Channel Annotations', type: 'channel_annotations_a2', status: 'active' },
    { name: 'Channel Cards', type: 'channel_cards_a2', status: 'active' },
    { name: 'Channel End Screens', type: 'channel_end_screens_a2', status: 'active' },
    { name: 'Channel Top Videos', type: 'channel_top_videos_a2', status: 'active' },
    { name: 'Channel Subtitles', type: 'channel_subtitles_a2', status: 'active' },
    { name: 'Channel Combined', type: 'channel_combined_a2', status: 'active' },
    { name: 'Channel Playback Location', type: 'channel_playback_location_a2', status: 'active' },
    { name: 'Playlist Basic', type: 'playlist_basic_a1', status: 'active' },
    { name: 'Playlist Province', type: 'playlist_province_a1', status: 'active' },
    { name: 'Playlist Traffic Source', type: 'playlist_traffic_source_a1', status: 'active' },
    { name: 'Playlist Device/OS', type: 'playlist_device_os_a1', status: 'active' },
    { name: 'Playlist Playback Location', type: 'playlist_playback_location_a1', status: 'active' },
    { name: 'Playlist Combined', type: 'playlist_combined_a1', status: 'active' }
  ];

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

  // Prepare demographics data for charts with comprehensive fallback
  const demographicsData = React.useMemo(() => {
    // Try comprehensive data first
    if (comprehensiveData?.demographics && comprehensiveData.demographics.length > 0) {
      const validDemographics = comprehensiveData.demographics.filter(
        (item: any) => item.age_group && item.gender && item.views_percentage > 0
      );
      
      if (validDemographics.length > 0) {
        return validDemographics.map((item: any) => ({
          name: `${item.age_group}-${item.gender}`,
          value: item.views_percentage || 0
        }));
      }
    }
    
    // Fallback to reporting data
    if (!reportingData?.demographics) return [];
    
    const validDemographics = reportingData.demographics.filter(
      (item: any) => item.age_group !== 'UNKNOWN' && parseFloat(item.total_views_pct) > 0
    );
    
    if (validDemographics.length === 0) {
      return [{ name: 'Data Collection in Progress', value: 100 }];
    }
    
    return validDemographics.map((item: any) => ({
      name: `${item.age_group}-${item.gender}`,
      value: parseFloat(item.total_views_pct) || 0
    }));
  }, [reportingData?.demographics, comprehensiveData?.demographics]);

  // Prepare device data for charts with comprehensive fallback
  const deviceData = React.useMemo(() => {
    // Try comprehensive data first
    if (comprehensiveData?.devices && comprehensiveData.devices.length > 0) {
      const validDevices = comprehensiveData.devices.filter(
        (item: any) => item.device_type && item.views_percentage > 0
      );
      
      if (validDevices.length > 0) {
        return validDevices.map((item: any) => ({
          device: item.device_type,
          os: item.operating_system || '',
          percentage: item.views_percentage || 0
        }));
      }
    }
    
    // Fallback to reporting data
    if (!reportingData?.devices) return [];
    
    const validDevices = reportingData.devices.filter(
      (item: any) => item.device_type !== 'UNKNOWN' && item.operating_system !== 'UNKNOWN'
    );
    
    if (validDevices.length === 0) {
      return [{ device: 'Data Collection in Progress', os: '', percentage: 100 }];
    }
    
    // Convert view counts to percentages
    const totalViews = validDevices.reduce((sum: number, item: any) => 
      sum + parseInt(item.views_pct || 0), 0);
    
    return validDevices.map((item: any) => ({
      device: item.device_type,
      os: item.operating_system,
      percentage: totalViews > 0 ? (parseInt(item.views_pct || 0) / totalViews) * 100 : 0
    }));
  }, [reportingData?.devices, comprehensiveData?.devices]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle color="success" fontSize="small" />;
      case 'pending':
        return <Schedule color="warning" fontSize="small" />;
      case 'error':
        return <Error color="error" fontSize="small" />;
      default:
        return <Schedule color="disabled" fontSize="small" />;
    }
  };

  const getStatusColor = (status: string): "success" | "warning" | "error" | "default" => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  // Admin Data Browser
  const [schema, setSchema] = React.useState('youtube');
  const [table, setTable] = React.useState('');
  const { data: schemasList } = useQuery({
    queryKey: ['data-schemas'],
    queryFn: async () => (await dataAPI.getInventory(undefined, true)),
    staleTime: 60000,
  });
  const { data: tablesList } = useQuery({
    queryKey: ['data-tables', schema],
    queryFn: async () => (await dataAPI.listTables(schema, false)),
    enabled: !!schema,
    staleTime: 60000,
  });
  const { data: columns } = useQuery({
    queryKey: ['data-columns', schema, table],
    queryFn: async () => (await (await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/data/columns/${schema}/${table}`)).json()),
    enabled: !!schema && !!table,
    staleTime: 60000,
  });
  const { data: sample } = useQuery({
    queryKey: ['data-sample', schema, table],
    queryFn: () => dataAPI.sampleTable(schema, table, 25, 0),
    enabled: !!schema && !!table,
    staleTime: 60000,
  });

  return (
    <Box>
      <SectionTitle>YOUTUBE REPORTING API :: DATA COLLECTION</SectionTitle>
      
      <Alert severity="success" sx={{ mb: 3 }}>
        YouTube Reporting API is active and collecting detailed analytics data daily. Last data processed: {reportingData?.summary?.days_with_data || 0} days available.
      </Alert>

      {/* Summary Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              {isLoading ? (
                <Skeleton height={80} />
              ) : (
                <>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Visibility color="primary" />
                    <Typography color="textSecondary" variant="caption">
                      Period Views
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={600}>
                    {formatNumber(comprehensiveData?.metrics?.period_views || reportingData?.summary?.period_views || 0)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {reportingData?.period || '30 days'}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              {isLoading ? (
                <Skeleton height={80} />
              ) : (
                <>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Schedule color="secondary" />
                    <Typography color="textSecondary" variant="caption">
                      Watch Time (Hours)
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={600}>
                    {formatNumber(Math.round((comprehensiveData?.metrics?.period_watch_time || reportingData?.summary?.period_watch_time || 0) / 60))}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatNumber(Math.round((reportingData?.summary?.avg_daily_watch_time || 0) / 60))}/day avg
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              {isLoading ? (
                <Skeleton height={80} />
              ) : (
                <>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <TrendingUp color="success" />
                    <Typography color="textSecondary" variant="caption">
                      Engagement
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={600}>
                    {formatNumber(comprehensiveData?.metrics?.period_likes || reportingData?.summary?.period_likes || 0)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    likes + {formatNumber(reportingData?.summary?.period_comments || 0)} comments
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              {isLoading ? (
                <Skeleton height={80} />
              ) : (
                <>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <People color="warning" />
                    <Typography color="textSecondary" variant="caption">
                      New Subscribers
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={600}>
                    {formatNumber(comprehensiveData?.metrics?.period_subscriber_gains || reportingData?.summary?.period_subscriber_gains || 0)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    from reporting data
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Admin: Data Browser */}
      <Panel label="ADMIN DATA BROWSER (YOUTUBE SCHEMAS)" sx={{ mt: 3 }}>
        <Box display="flex" gap={2} mb={2}>
          <TextField size="small" select label="Schema" value={schema} onChange={(e) => { setSchema(e.target.value); setTable(''); }}>
            {['youtube','personal_youtube','reporting_personal_youtube','dashboard'].map(s => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
          <TextField size="small" select label="Table" value={table} onChange={(e) => setTable(e.target.value)} sx={{ minWidth: 240 }}>
            {(tablesList?.tables || []).map((t: any) => (
              <MenuItem key={t.table} value={t.table}>{t.table} ({t.row_estimate})</MenuItem>
            ))}
          </TextField>
        </Box>
        {table && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>Columns</Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
              {(columns?.columns || []).map((c: any) => (
                <Chip key={c.name} label={`${c.name}: ${c.type}`} />
              ))}
            </Box>
            <Typography variant="subtitle2" gutterBottom>Sample Rows</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {(columns?.columns || []).map((c: any) => (
                    <TableCell key={c.name}>{c.name}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(sample?.rows || []).map((row: any, idx: number) => (
                  <TableRow key={idx}>
                    {(columns?.columns || []).map((c: any) => (
                      <TableCell key={c.name} sx={{ maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {String(row[c.name] ?? '')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Panel>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3, mt: 3 }}>
        {/* Demographics Chart */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="AUDIENCE DEMOGRAPHICS" sx={{ height: 400 }}>
            {isLoading ? (
              <Skeleton variant="rectangular" height={320} />
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={demographicsData.slice(0, 6)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => 
                      demographicsData.length === 1 && demographicsData[0].name === 'Data Collection in Progress' 
                        ? name 
                        : `${name}: ${value?.toFixed(1) || 0}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {demographicsData.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => 
                    demographicsData.length === 1 && demographicsData[0].name === 'Data Collection in Progress' 
                      ? ['', 'Status'] 
                      : [`${value.toFixed(1)}%`, 'Views']
                  } />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </Grid>

        {/* Device/OS Breakdown */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="DEVICE & OPERATING SYSTEM" sx={{ height: 400 }}>
            {isLoading ? (
              <Skeleton variant="rectangular" height={320} />
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={deviceData.slice(0, 8)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="device" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis tickFormatter={(value) => `${value}%`} />
                  <Tooltip 
                    formatter={(value: number) => 
                      deviceData.length === 1 && deviceData[0].device === 'Data Collection in Progress' 
                        ? ['', 'Status'] 
                        : [`${value.toFixed(1)}%`, 'Views']
                    }
                    labelFormatter={(label) => 
                      deviceData.length === 1 && deviceData[0].device === 'Data Collection in Progress' 
                        ? label 
                        : `Device: ${label}`
                    }
                  />
                  <Bar dataKey="percentage" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </Grid>
      </Grid>

      {/* Active Reports Grid */}
      <Panel label={`ACTIVE REPORT TYPES (${reports.length} TOTAL)`}>
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {reports.map((report) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={report.type}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="subtitle2" fontWeight={500}>
                      {report.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {report.type}
                    </Typography>
                  </Box>
                  <Chip
                    icon={getStatusIcon(report.status)}
                    label={report.status}
                    size="small"
                    color={getStatusColor(report.status)}
                    variant="outlined"
                  />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Box mt={3}>
          <Typography variant="body2" color="textSecondary">
            Reports are automatically downloaded daily at 3 AM UTC. Data processing happens immediately after download.
            All reporting data is stored in dedicated schemas for comprehensive analytics.
          </Typography>
        </Box>
      </Panel>
    </Box>
  );
};

export default ReportingData;
